import React, { useState, useEffect } from 'react';
import { api } from '../../../utils/api';
import { toast } from 'react-toastify';
import {
  Users, DollarSign, Calendar, Plus, Trash2, Edit2, Check, X,
  UserPlus, Award, UserCheck, ShieldAlert, FileText, ChevronRight
} from 'lucide-react';

interface Employee {
  id: number;
  name: string;
  phone: string | null;
  type: 'planta' | 'turnos';
  salary_rate: number;
  active: boolean;
}

interface PayrollPayment {
  id: number;
  employee_id: number;
  paid_at: string;
  period_start: string;
  period_end: string;
  units_worked: number;
  amount: number;
  notes: string | null;
  employee: Employee;
}

const fmt = (n: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return datePart;
  return `${d}/${m}/${y}`;
};

export default function PayrollPage() {
  const [activeTab, setActiveTab] = useState<'employees' | 'payments'>('employees');
  
  // State for Employees
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [empForm, setEmpForm] = useState({
    name: '',
    phone: '',
    type: 'planta' as 'planta' | 'turnos',
    salary_rate: 0,
    active: true
  });

  // State for Payments
  const [payments, setPayments] = useState<PayrollPayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payForm, setPayForm] = useState({
    employee_id: '',
    paid_at: new Date().toISOString().slice(0, 10),
    period_start: new Date(Date.now() - 15 * 86400000).toISOString().slice(0, 10),
    period_end: new Date().toISOString().slice(0, 10),
    units_worked: 1,
    amount: 0,
    notes: ''
  });

  // Load Data
  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const { data } = await api.get('/purchases/employees');
      setEmployees(data);
    } catch (e) {
      toast.error('Error al cargar empleados');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadPayments = async () => {
    setLoadingPayments(true);
    try {
      const { data } = await api.get('/purchases/payroll-payments');
      setPayments(data);
    } catch (e) {
      toast.error('Error al cargar pagos de nómina');
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    loadPayments();
  }, []);

  // Handle Employee Form Submit
  const handleSaveEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empForm.name.trim()) return toast.error('El nombre es requerido');
    
    try {
      if (editingEmployee) {
        await api.put(`/purchases/employees/${editingEmployee.id}`, empForm);
        toast.success('Empleado actualizado correctamente');
      } else {
        await api.post('/purchases/employees', empForm);
        toast.success('Empleado creado correctamente');
      }
      setShowEmployeeModal(false);
      setEditingEmployee(null);
      setEmpForm({ name: '', phone: '', type: 'planta', salary_rate: 0, active: true });
      loadEmployees();
    } catch (err) {
      toast.error('Error al guardar empleado');
    }
  };

  // Open Edit Employee
  const handleEditEmployee = (emp: Employee) => {
    setEditingEmployee(emp);
    setEmpForm({
      name: emp.name,
      phone: emp.phone ?? '',
      type: emp.type,
      salary_rate: emp.salary_rate,
      active: emp.active
    });
    setShowEmployeeModal(true);
  };

  // Delete Employee
  const handleDeleteEmployee = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar/desactivar este empleado?')) return;
    try {
      await api.delete(`/purchases/employees/${id}`);
      toast.success('Empleado procesado correctamente');
      loadEmployees();
    } catch (err) {
      toast.error('Error al eliminar empleado');
    }
  };

  // Handle Payment employee selection to auto calculate amount
  const handlePayEmployeeChange = (empId: string) => {
    const emp = employees.find(e => e.id === Number(empId));
    if (emp) {
      const calculatedAmount = emp.type === 'planta' ? emp.salary_rate : emp.salary_rate * payForm.units_worked;
      setPayForm(prev => ({
        ...prev,
        employee_id: empId,
        amount: calculatedAmount
      }));
    } else {
      setPayForm(prev => ({ ...prev, employee_id: empId, amount: 0 }));
    }
  };

  // Handle units worked change for shift workers
  const handleUnitsChange = (units: number) => {
    const emp = employees.find(e => e.id === Number(payForm.employee_id));
    if (emp) {
      const calculatedAmount = emp.type === 'planta' ? emp.salary_rate : emp.salary_rate * units;
      setPayForm(prev => ({
        ...prev,
        units_worked: units,
        amount: calculatedAmount
      }));
    } else {
      setPayForm(prev => ({ ...prev, units_worked: units }));
    }
  };

  // Handle Save Payment
  const handleSavePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payForm.employee_id) return toast.error('Selecciona un empleado');
    if (payForm.amount <= 0) return toast.error('El monto debe ser mayor a 0');

    try {
      await api.post('/purchases/payroll-payments', payForm);
      toast.success('Pago de nómina registrado');
      setShowPaymentModal(false);
      setPayForm({
        employee_id: '',
        paid_at: new Date().toISOString().slice(0, 10),
        period_start: new Date(Date.now() - 15 * 86400000).toISOString().slice(0, 10),
        period_end: new Date().toISOString().slice(0, 10),
        units_worked: 1,
        amount: 0,
        notes: ''
      });
      loadPayments();
    } catch (err) {
      toast.error('Error al registrar pago');
    }
  };

  // Delete Payment
  const handleDeletePayment = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este registro de pago?')) return;
    try {
      await api.delete(`/purchases/payroll-payments/${id}`);
      toast.success('Registro de pago eliminado');
      loadPayments();
    } catch (err) {
      toast.error('Error al eliminar pago');
    }
  };

  const activeEmployees = employees.filter(e => e.active);
  const monthlyCostEstimate = employees.reduce((acc, emp) => {
    // Estimación mensual aproximada
    return acc + (emp.type === 'planta' ? emp.salary_rate : emp.salary_rate * 30); // asumiendo 30 turnos para estimación rápida
  }, 0);

  return (
    <div className="min-h-screen bg-bg px-4 py-6 md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Control de Nómina</h1>
            <p className="text-sm text-text-muted">Administra el personal operativo de planta y turnos y sus pagos</p>
          </div>
          <div className="flex gap-2">
            {activeTab === 'employees' ? (
              <button
                onClick={() => {
                  setEditingEmployee(null);
                  setEmpForm({ name: '', phone: '', type: 'planta', salary_rate: 0, active: true });
                  setShowEmployeeModal(true);
                }}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-white shadow hover:opacity-90 transition"
              >
                <UserPlus size={16} /> Nuevo Empleado
              </button>
            ) : (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-white shadow hover:opacity-90 transition"
              >
                <Plus size={16} /> Registrar Pago
              </button>
            )}
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('employees')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition border-b-2 -mb-[2px] ${
              activeTab === 'employees'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text'
            }`}
          >
            <Users size={16} /> Personal ({employees.length})
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition border-b-2 -mb-[2px] ${
              activeTab === 'payments'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text'
            }`}
          >
            <DollarSign size={16} /> Pagos de Nómina ({payments.length})
          </button>
        </div>

        {/* --- EMPLOYEES TAB --- */}
        {activeTab === 'employees' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-card">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Users size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Total Empleados</p>
                  <p className="text-2xl font-bold text-text">{employees.length}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-card">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <UserCheck size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Activos</p>
                  <p className="text-2xl font-bold text-text">{activeEmployees.length}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-card">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Award size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">De Planta / Turnos</p>
                  <p className="text-sm font-bold text-text">
                    {employees.filter(e => e.type === 'planta').length} Planta / {employees.filter(e => e.type === 'turnos').length} Turnos
                  </p>
                </div>
              </div>
            </div>

            {/* List */}
            {loadingEmployees ? (
              <div className="flex h-40 items-center justify-center text-muted">Cargando empleados...</div>
            ) : employees.length === 0 ? (
              <div className="rounded-2xl border border-border bg-surface p-10 text-center text-text-muted">
                No hay empleados registrados. ¡Crea el primero!
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {employees.map(emp => (
                  <div key={emp.id} className="relative rounded-2xl border border-border bg-surface p-5 shadow-card hover:border-primary/50 transition">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-text text-lg">{emp.name}</h3>
                        <p className="text-xs text-text-muted mt-0.5">{emp.phone || 'Sin teléfono'}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        emp.type === 'planta' ? 'bg-indigo-150 text-indigo-750 bg-indigo-50' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {emp.type === 'planta' ? 'Planta' : 'Por Turnos'}
                      </span>
                    </div>

                    <div className="mt-4 pt-4 border-t border-divider flex justify-between items-center">
                      <div>
                        <p className="text-xs text-text-muted">Tarifa / Salario Base</p>
                        <p className="text-lg font-bold text-primary">{fmt(emp.salary_rate)} <span className="text-xs font-normal text-text-muted">{emp.type === 'turnos' ? '/ turno' : '/ mes'}</span></p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditEmployee(emp)}
                          className="p-2 rounded-lg text-text-muted hover:bg-muted transition"
                          title="Editar"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          onClick={() => handleDeleteEmployee(emp.id)}
                          className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition"
                          title="Eliminar"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>

                    {!emp.active && (
                      <div className="absolute inset-0 bg-surface/80 rounded-2xl flex items-center justify-center">
                        <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Inactivo</span>
                        <button onClick={() => handleEditEmployee({...emp, active: true})} className="absolute top-3 right-3 p-2 bg-primary/10 text-primary rounded-full hover:bg-primary/20">
                          Activar
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- PAYMENTS TAB --- */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-card">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100 text-red-650 bg-red-50">
                  <DollarSign size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Total Pagado (Histórico)</p>
                  <p className="text-2xl font-bold text-text">{fmt(payments.reduce((s, p) => s + p.amount, 0))}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-card">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                  <Calendar size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Pagos Realizados</p>
                  <p className="text-2xl font-bold text-text">{payments.length}</p>
                </div>
              </div>
            </div>

            {/* List / Table */}
            {loadingPayments ? (
              <div className="flex h-40 items-center justify-center text-muted">Cargando pagos...</div>
            ) : payments.length === 0 ? (
              <div className="rounded-2xl border border-border bg-surface p-10 text-center text-text-muted">
                No hay pagos registrados en el sistema.
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-card">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-border bg-muted/30 text-xs font-semibold uppercase tracking-wider text-text-muted">
                        <th className="px-5 py-4">Empleado</th>
                        <th className="px-5 py-4">Fecha Pago</th>
                        <th className="px-5 py-4">Periodo</th>
                        <th className="px-5 py-4 text-center">Unidades / Turnos</th>
                        <th className="px-5 py-4 text-right">Monto</th>
                        <th className="px-5 py-4">Notas</th>
                        <th className="px-5 py-4 text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border text-sm text-text">
                      {payments.map(pay => (
                        <tr key={pay.id} className="hover:bg-muted/10 transition">
                          <td className="px-5 py-4 font-medium">
                            <div>
                              <p className="font-bold">{pay.employee?.name || 'Empleado Eliminado'}</p>
                              <span className={`text-[10px] px-1.5 py-0.2 rounded font-semibold uppercase ${
                                pay.employee?.type === 'planta' ? 'bg-indigo-50 text-indigo-650' : 'bg-amber-50 text-amber-650'
                              }`}>
                                {pay.employee?.type || ''}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">{formatDate(pay.paid_at)}</td>
                          <td className="px-5 py-4 whitespace-nowrap text-xs text-text-muted">
                            {formatDate(pay.period_start)} al {formatDate(pay.period_end)}
                          </td>
                          <td className="px-5 py-4 text-center">{pay.units_worked}</td>
                          <td className="px-5 py-4 text-right font-bold text-primary">{fmt(pay.amount)}</td>
                          <td className="px-5 py-4 text-xs text-text-muted max-w-[200px] truncate">{pay.notes || '—'}</td>
                          <td className="px-5 py-4 text-center">
                            <button
                              onClick={() => handleDeletePayment(pay.id)}
                              className="text-red-500 p-1.5 rounded hover:bg-red-50 transition"
                              title="Borrar pago"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* --- EMPLOYEE MODAL --- */}
        {showEmployeeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-2xl border border-border animate-in fade-in zoom-in-95">
              <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
                <h2 className="text-lg font-bold text-text">{editingEmployee ? 'Editar Empleado' : 'Nuevo Empleado'}</h2>
                <button onClick={() => setShowEmployeeModal(false)} className="rounded-lg p-1 text-text-muted hover:bg-muted">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSaveEmployee} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Nombre Completo</label>
                  <input
                    type="text"
                    required
                    value={empForm.name}
                    onChange={e => setEmpForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full rounded-xl border border-border bg-bg px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Teléfono</label>
                  <input
                    type="text"
                    value={empForm.phone}
                    onChange={e => setEmpForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full rounded-xl border border-border bg-bg px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Tipo de Nómina</label>
                    <select
                      value={empForm.type}
                      onChange={e => setEmpForm(prev => ({ ...prev, type: e.target.value as 'planta' | 'turnos' }))}
                      className="w-full rounded-xl border border-border bg-bg px-3 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                      <option value="planta">Planta (Mensual)</option>
                      <option value="turnos">Por Turno / Hora</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">
                      {empForm.type === 'planta' ? 'Salario Mensual' : 'Valor Turno'}
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={empForm.salary_rate}
                      onChange={e => setEmpForm(prev => ({ ...prev, salary_rate: Number(e.target.value) }))}
                      className="w-full rounded-xl border border-border bg-bg px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>

                {editingEmployee && (
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="active"
                      checked={empForm.active}
                      onChange={e => setEmpForm(prev => ({ ...prev, active: e.target.checked }))}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <label htmlFor="active" className="text-sm font-semibold text-text">Empleado Activo</label>
                  </div>
                )}

                <div className="flex justify-end gap-2 border-t border-border pt-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowEmployeeModal(false)}
                    className="h-10 rounded-xl px-4 text-sm font-semibold text-text-muted hover:bg-muted"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="h-10 rounded-xl bg-primary px-5 text-sm font-bold text-white shadow hover:opacity-90"
                  >
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- REGISTRO PAGO MODAL --- */}
        {showPaymentModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-2xl border border-border animate-in fade-in zoom-in-95">
              <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
                <h2 className="text-lg font-bold text-text">Registrar Pago de Nómina</h2>
                <button onClick={() => setShowPaymentModal(false)} className="rounded-lg p-1 text-text-muted hover:bg-muted">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSavePayment} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Empleado</label>
                  <select
                    required
                    value={payForm.employee_id}
                    onChange={e => handlePayEmployeeChange(e.target.value)}
                    className="w-full rounded-xl border border-border bg-bg px-3 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="">Selecciona un empleado...</option>
                    {activeEmployees.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.name} ({e.type === 'planta' ? 'Planta' : 'Turnos'} — Tarifa: {fmt(e.salary_rate)})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Unidades / Turnos</label>
                    <input
                      type="number"
                      required
                      min="0.1"
                      step="0.1"
                      value={payForm.units_worked}
                      onChange={e => handleUnitsChange(Number(e.target.value))}
                      className="w-full rounded-xl border border-border bg-bg px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Total a Pagar (COP)</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={payForm.amount}
                      onChange={e => setPayForm(prev => ({ ...prev, amount: Number(e.target.value) }))}
                      className="w-full rounded-xl border border-border bg-bg px-3.5 py-2.5 text-sm font-bold text-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Periodo Inicio</label>
                    <input
                      type="date"
                      required
                      value={payForm.period_start}
                      onChange={e => setPayForm(prev => ({ ...prev, period_start: e.target.value }))}
                      className="w-full rounded-xl border border-border bg-bg px-3.5 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Periodo Fin</label>
                    <input
                      type="date"
                      required
                      value={payForm.period_end}
                      onChange={e => setPayForm(prev => ({ ...prev, period_end: e.target.value }))}
                      className="w-full rounded-xl border border-border bg-bg px-3.5 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Fecha Pago</label>
                    <input
                      type="date"
                      required
                      value={payForm.paid_at}
                      onChange={e => setPayForm(prev => ({ ...prev, paid_at: e.target.value }))}
                      className="w-full rounded-xl border border-border bg-bg px-3.5 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Observaciones</label>
                    <input
                      type="text"
                      value={payForm.notes}
                      placeholder="Ej: Pago quincena / Bono"
                      onChange={e => setPayForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full rounded-xl border border-border bg-bg px-3.5 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-border pt-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="h-10 rounded-xl px-4 text-sm font-semibold text-text-muted hover:bg-muted"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="h-10 rounded-xl bg-primary px-5 text-sm font-bold text-white shadow hover:opacity-90"
                  >
                    Registrar Pago
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
